// Patient gender enum (existing field)
export enum PatientGender {
  FEMALE = 0,
  MALE = 1,
}

// MCC Registry sex enum (EUROCAT standard)
export enum PatientSex {
  MALE = 1,
  FEMALE = 2,
  UNKNOWN = 3,
}

// Birth outcome enum
export enum BirthOutcome {
  LIVE_BIRTH = 1,
  STILLBIRTH = 2,
  TOPFA = 3, // Termination of pregnancy for fetal anomaly
}

// MCC Subgroup enum
export enum CHDSubgroup {
  TGA = 1, // Transposition of great arteries
  DORV = 2, // Double outlet right ventricle
  TAC = 3, // Common arterial trunk
  AP_SEPT_INTACT = 4, // Pulmonary atresia with intact septum
  ATV = 5, // Tricuspid atresia
  HLHS = 6, // Hypoplastic left heart syndrome
  COA = 7, // Coarctation of aorta
  IAA = 8, // Interrupted aortic arch
  SAO_CRITICA = 9, // Critical aortic stenosis
  PDA_MAJOR = 10, // Major patent ductus arteriosus
  ASD_MAJOR = 11, // Major atrial septal defect
  VSD_MAJOR = 12, // Major ventricular septal defect
  AVSD = 13, // Atrioventricular septal defect
  TAPVC_PAPVC = 14, // Anomalous pulmonary venous connection
  TOF = 15, // Tetralogy of Fallot
  SP_CRITICA = 16, // Critical pulmonary stenosis
  OTHER_MAJOR = 17, // Other major MCC
}

// Prenatal diagnosis method enum
export enum PrenatalMethod {
  US = 1, // Ultrasound
  NIPT = 2, // Non-invasive prenatal testing
  CVS = 3, // Chorionic villus sampling
  AMNIOCENTESIS = 4,
  OTHER = 5,
}

// 30-day outcome enum
export enum Outcome30Day {
  SURVIVOR = 1,
  DECEASED = 2,
}

// EUROCAT classification
export enum MajorMinorFlag {
  MAJOR = 1,
  MINOR = 2,
}

// Index procedure type
export enum ProcedureType {
  SURGICAL = 1,
  INTERVENTIONAL = 2,
}

// Data source enum
export enum DataSource {
  NEONATOLOGY = 1,
  PEDIATRIC_CARDIOLOGY = 2,
  CARDIAC_SURGERY = 3,
  PATHOLOGY = 4,
  PRENATAL_SCREENING = 5,
  BIRTH_REGISTRY = 6,
  OTHER = 7,
}

// Centre (reporting center) enum
export enum Centre {
  BUCURESTI = 1,
  TG_MURES = 2,
  CLUJ = 3,
  IASI = 4,
  TIMISOARA = 5,
  CRAIOVA = 6,
  BRASOV = 7,
  SIBIU = 8,
}

// Patient interface with MCC registry fields
export interface Patient {
  // Existing fields
  id: number;
  patient_code: string;
  patient_guid?: string;
  name: string;
  cnp?: string;
  birth_date: string;
  gender: PatientGender; // Keep for backward compatibility
  birth_weight?: number;
  gestational_age?: number;
  hospital_id: number;
  hospital_name?: string;
  external_id?: string;
  county?: string; // judet_nastere (text field, kept for display)
  locality?: string; // localitate_nastere (text field, kept for display)
  maternity?: string; // maternitate_nastere
  id_judet?: number | null; // Foreign key to judete table
  id_localitate?: number | null; // Foreign key to localitati table
  mother_name?: string;
  father_name?: string;
  notes?: string;
  created_at: string;
  updated_at: string;

  // MCC Registry fields
  case_id?: string; // UUID for MCC registry
  centre?: number;
  sex?: PatientSex; // EUROCAT standard sex field
  ga_weeks?: number; // Gestational age in weeks (22-43)
  bw_grams?: number; // Birth weight in grams (300-6000)
  birth_outcome?: BirthOutcome;
  subgroup_chd?: CHDSubgroup;
  icd10_code?: string;
  severe_chd?: boolean;
  mca_flag?: boolean; // Multiple congenital anomalies
  syndrome?: string; // Altă afecțiune genetică (text) - fallback when no ICD-10 code applies
  genetic_condition_icd10_code?: string | null; // ICD-10 code from icd10_genetic_codes
  prenatal_dx?: boolean;
  prenatal_method?: PrenatalMethod;
  ga_prenatal_dx?: number; // GA at prenatal diagnosis (10-40)
  outcome_30d?: Outcome30Day;
  reintervention_1y?: boolean;
  procedure_type?: ProcedureType;
  procedure_date?: string;
  major_minor_flag?: MajorMinorFlag;
  data_source?: DataSource;
  data_quality_note?: string;
  other_anomalies_icd10?: string; // Comma-separated ICD-10 codes

  maternal_data?: MaternalData;

  // Ownership/takeover fields
  is_owned?: boolean;
  owned_by_hospital_id?: number;
  original_hospital_id?: number;

  // Whether any VON (premature) data has been recorded for this patient
  has_premature_data?: boolean;
  takeover_count?: number;
  preluat_la_data?: string;
  can_takeover?: boolean;
  takeover_reason?: string | null;

  // Premature patient flag
  is_premature?: number; // 0 = not premature, 1 = premature
}

// Patient creation/update data
export interface PatientData {
  // Existing fields
  name: string;
  cnp?: string;
  birth_date: string;
  gender: PatientGender;
  birth_weight?: number | null;
  gestational_age?: number | null;
  hospital_id: number;
  patient_code?: string;
  external_id?: string;
  county?: string;
  locality?: string;
  maternity?: string;
  id_judet?: number | null;
  id_localitate?: number | null;
  mother_name?: string;
  father_name?: string;
  notes?: string;

  // MCC Registry fields
  case_id?: string;
  centre?: number;
  sex?: PatientSex;
  ga_weeks?: number;
  bw_grams?: number;
  birth_outcome?: BirthOutcome;
  subgroup_chd?: CHDSubgroup;
  icd10_code?: string;
  severe_chd?: boolean;
  mca_flag?: boolean;
  syndrome?: string;
  genetic_condition_icd10_code?: string | null;
  prenatal_dx?: boolean;
  prenatal_method?: PrenatalMethod;
  ga_prenatal_dx?: number;
  outcome_30d?: Outcome30Day;
  reintervention_1y?: boolean;
  procedure_type?: ProcedureType;
  procedure_date?: string;
  major_minor_flag?: MajorMinorFlag;
  data_source?: DataSource;
  data_quality_note?: string;
  other_anomalies_icd10?: string;

  maternal_data?: Partial<MaternalData> | null;

  // Premature patient flag
  is_premature?: number; // 0 = not premature, 1 = premature
}

// Patient filters for search
export interface PatientFilters {
  page?: number;
  limit?: number;
  search?: string;
  cnp?: string;
  hospital_id?: number;
  gender?: PatientGender;
  sex?: PatientSex;
  birth_date_from?: string;
  birth_date_to?: string;
  severe_chd?: boolean;
  subgroup_chd?: CHDSubgroup;
  centre?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  owned?: boolean; // Filter for owned patients only
  has_premature_data?: boolean; // Filter for patients with premature data (deprecated - use is_premature)
  is_premature?: boolean; // Filter for patients marked as premature
}

export interface MaternalData {
  id?: number;
  maternal_age?: number | null;
  maternal_conditions_diabetes?: boolean;
  maternal_conditions_lupus?: boolean;
  maternal_conditions_hta?: boolean;
  maternal_conditions_other_active?: boolean;
  maternal_conditions_other?: string | null;
  exposures_smoking?: boolean;
  exposures_teratogenic_meds?: boolean;
  exposures_infection?: boolean;
  exposures_other?: boolean;
  exposures_other_details?: string | null;
  folate_supplementation?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// Patient statistics
export interface PatientStatistics {
  total_patients: number;
  by_gender: {
    [key in PatientGender]: number;
  };
  by_sex?: {
    [key in PatientSex]: number;
  };
  by_hospital: Array<{
    hospital_id: number;
    hospital_name: string;
    count: number;
  }>;
  by_month: Array<{
    month: string;
    count: number;
  }>;
  by_chd_subgroup?: Array<{
    subgroup: CHDSubgroup;
    count: number;
  }>;
  severe_chd_count?: number;
  prenatal_diagnosis_count?: number;
}

// Helper function to get MCC subgroup label
export const getCHDSubgroupLabel = (subgroup: CHDSubgroup): string => {
  const labels: Record<CHDSubgroup, string> = {
    [CHDSubgroup.TGA]: 'Transpoziția marilor artere',
    [CHDSubgroup.DORV]: 'Dublă cale de ieșire din VD',
    [CHDSubgroup.TAC]: 'Trunchi arterial comun',
    [CHDSubgroup.AP_SEPT_INTACT]: 'Atrezie pulmonară cu sept intact',
    [CHDSubgroup.ATV]: 'Atrezie tricuspidiană',
    [CHDSubgroup.HLHS]: 'Sindrom de cord stâng hipoplazic',
    [CHDSubgroup.COA]: 'Coarctație de aortă',
    [CHDSubgroup.IAA]: 'Întrerupere de arc aortic',
    [CHDSubgroup.SAO_CRITICA]: 'Stenoză aortică critică',
    [CHDSubgroup.PDA_MAJOR]: 'Persistența canalului arterial major',
    [CHDSubgroup.ASD_MAJOR]: 'Defect septal atrial major',
    [CHDSubgroup.VSD_MAJOR]: 'Defect septal ventricular major',
    [CHDSubgroup.AVSD]: 'Defect septal atrioventricular',
    [CHDSubgroup.TAPVC_PAPVC]: 'Conexiune venoasă pulmonară anormală',
    [CHDSubgroup.TOF]: 'Tetralogia Fallot',
    [CHDSubgroup.SP_CRITICA]: 'Stenoză pulmonară critică',
    [CHDSubgroup.OTHER_MAJOR]: 'Alte MCC majore',
  };
  return labels[subgroup] || 'Necunoscut';
};

// Helper function to get centre label
export const getCentreLabel = (centre: Centre): string => {
  const labels: Record<Centre, string> = {
    [Centre.BUCURESTI]: 'București',
    [Centre.TG_MURES]: 'Târgu Mureș',
    [Centre.CLUJ]: 'Cluj-Napoca',
    [Centre.IASI]: 'Iași',
    [Centre.TIMISOARA]: 'Timișoara',
    [Centre.CRAIOVA]: 'Craiova',
    [Centre.BRASOV]: 'Brașov',
    [Centre.SIBIU]: 'Sibiu',
  };
  return labels[centre] || 'Necunoscut';
};

// Helper functions for Romanian labels
export const getPatientSexLabel = (sex: PatientSex): string => {
  const labels: Record<PatientSex, string> = {
    [PatientSex.MALE]: 'Masculin',
    [PatientSex.FEMALE]: 'Feminin',
    [PatientSex.UNKNOWN]: 'Necunoscut',
  };
  return labels[sex] || 'Necunoscut';
};

export const getBirthOutcomeLabel = (outcome: BirthOutcome): string => {
  const labels: Record<BirthOutcome, string> = {
    [BirthOutcome.LIVE_BIRTH]: 'Născut viu',
    [BirthOutcome.STILLBIRTH]: 'Născut mort',
    [BirthOutcome.TOPFA]: 'Întrerupere pentru anomalie fetală',
  };
  return labels[outcome] || 'Necunoscut';
};

export const getPrenatalMethodLabel = (method: PrenatalMethod): string => {
  const labels: Record<PrenatalMethod, string> = {
    [PrenatalMethod.US]: 'Ecografie',
    [PrenatalMethod.NIPT]: 'Test prenatal non-invaziv',
    [PrenatalMethod.CVS]: 'Biopsie de vilozități coriale',
    [PrenatalMethod.AMNIOCENTESIS]: 'Amniocenteză',
    [PrenatalMethod.OTHER]: 'Altă metodă',
  };
  return labels[method] || 'Necunoscut';
};

export const getOutcome30DayLabel = (outcome: Outcome30Day): string => {
  const labels: Record<Outcome30Day, string> = {
    [Outcome30Day.SURVIVOR]: 'Supraviețuitor',
    [Outcome30Day.DECEASED]: 'Decedat',
  };
  return labels[outcome] || 'Necunoscut';
};

export const getMajorMinorFlagLabel = (flag: MajorMinorFlag): string => {
  const labels: Record<MajorMinorFlag, string> = {
    [MajorMinorFlag.MAJOR]: 'Major',
    [MajorMinorFlag.MINOR]: 'Minor',
  };
  return labels[flag] || 'Necunoscut';
};

export const getProcedureTypeLabel = (type: ProcedureType): string => {
  const labels: Record<ProcedureType, string> = {
    [ProcedureType.SURGICAL]: 'Intervenție chirurgicală',
    [ProcedureType.INTERVENTIONAL]: 'Procedură intervențională',
  };
  return labels[type] || 'Necunoscut';
};

export const getDataSourceLabel = (source: DataSource): string => {
  const labels: Record<DataSource, string> = {
    [DataSource.NEONATOLOGY]: 'Neonatologie',
    [DataSource.PEDIATRIC_CARDIOLOGY]: 'Cardiologie Pediatrică',
    [DataSource.CARDIAC_SURGERY]: 'Chirurgie Cardiacă',
    [DataSource.PATHOLOGY]: 'Anatomie Patologică',
    [DataSource.PRENATAL_SCREENING]: 'Screening Prenatal',
    [DataSource.BIRTH_REGISTRY]: 'Registrul de Nașteri',
    [DataSource.OTHER]: 'Altă sursă',
  };
  return labels[source] || 'Necunoscut';
};
